import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Icon,
  Link,
  Flex,
  Divider,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import Loader from '../components/Loader';
import SEO from '../components/SEO';
import { 
  FaInstagram, 
  FaWhatsapp, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaPaperPlane,
  FaCheckCircle
} from 'react-icons/fa';
import authService from '../services/authService';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Add global styles for autofill on mobile - better visibility
const autofillStyles = `
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  textarea:-webkit-autofill:active {
    -webkit-box-shadow: inset 0 0 0px 1000px white !important;
    -webkit-text-fill-color: black !important;
    background-color: white !important;
    color: black !important;
    caret-color: black !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

// Inject the font and autofill styles
if (typeof document !== 'undefined') {
  // Font styles
  if (!document.head.querySelector('[data-contact-fonts]')) {
    const fontStyleElement = document.createElement('style');
    fontStyleElement.textContent = fontImport;
    fontStyleElement.setAttribute('data-contact-fonts', 'true');
    document.head.appendChild(fontStyleElement);
  }
  
  // Autofill styles
  if (!document.head.querySelector('[data-contact-autofill-styles]')) {
    const autofillStyleElement = document.createElement('style');
    autofillStyleElement.textContent = autofillStyles;
    autofillStyleElement.setAttribute('data-contact-autofill-styles', 'true');
    document.head.appendChild(autofillStyleElement);
  }
}

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    company: '',
    how_did_you_find_us: '',
    message: ''
  });
  
  const [config, setConfig] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Captcha state
  const [captcha, setCaptcha] = useState({ question: '', answer: null });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Generate simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operations = ['+', '-', '×'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let answer;
    let question;
    
    switch (operation) {
      case '+':
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case '-':
        // Ensure positive result
        const larger = Math.max(num1, num2);
        const smaller = Math.min(num1, num2);
        answer = larger - smaller;
        question = `${larger} - ${smaller}`;
        break;
      case '×':
        answer = num1 * num2;
        question = `${num1} × ${num2}`;
        break;
      default:
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
    }
    
    setCaptcha({ question, answer });
    setCaptchaInput('');
    setCaptchaError(false);
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoading(true);
        const contactConfig = await authService.getContactConfig();
        setConfig(contactConfig);
        generateCaptcha();
      } catch (err) {
        setError('Failed to load contact form. Please refresh the page.');
        console.error('Error fetching contact config:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Required fields validation
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone_number.trim()) errors.phone_number = 'Phone number is required';
    if (!formData.how_did_you_find_us) errors.how_did_you_find_us = 'Please select how you found us';
    if (!formData.message.trim()) errors.message = 'Message is required';
    
    // Format validations
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone_number && !/^\d{10}$/.test(formData.phone_number.replace(/\s/g, ''))) {
      errors.phone_number = 'Phone number must be exactly 10 digits';
    }
    
    // Length validations
    if (formData.name.length > 100) errors.name = 'Name cannot exceed 100 characters';
    if (formData.company.length > 150) errors.company = 'Company name cannot exceed 150 characters';
    if (formData.message.length > 1000) errors.message = 'Message cannot exceed 1000 characters';
    
    // Captcha validation
    if (parseInt(captchaInput) !== captcha.answer) {
      errors.captcha = 'Please solve the math question correctly';
      setCaptchaError(true);
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submissionData = {
        ...formData,
        phone_number: formData.phone_number.replace(/\s/g, '') // Remove any spaces
      };
      
      const result = await authService.submitContactForm(submissionData);
      
      // Success - show modal and reset form
      onOpen();
      setFormData({
        name: '',
        email: '',
        phone_number: '',
        company: '',
        how_did_you_find_us: '',
        message: ''
      });
      setFieldErrors({});
      generateCaptcha(); // Generate new captcha
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      if (error.details && typeof error.details === 'object') {
        // Field-specific validation errors from backend
        setFieldErrors(error.details);
        toast({
          title: "Validation Error",
          description: "Please fix the highlighted errors",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } else {
        // General error
        toast({
          title: "Submission Failed",
          description: error.error || "Something went wrong. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: FaEnvelope,
      label: 'Email Us',
      value: 'agnir.info@gmail.com',
      href: 'mailto:agnir.info@gmail.com',
      color: '#4285f4'
    },
    {
      icon: FaWhatsapp,
      label: 'WhatsApp',
      value: '+91 7387657127',
      href: 'https://wa.me/917387657127',
      color: '#25D366'
    },
    {
      icon: FaInstagram,
      label: 'Instagram',
      value: '@agnir.co',
      href: 'https://www.instagram.com/agnir.co?igsh=MW5mYmJiZ2VnYjlyYg==',
      color: '#E4405F'
    }
  ];

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={20}>
        <Loader minH="400px" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={20}>
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title="Contact Us - Get in Touch"
        description="Contact AGNIR for inquiries about our premium handcrafted jewelry collection. Reach out for custom orders, wholesale inquiries, or any questions about our rings, bracelets, chains, and pendants."
        keywords="contact AGNIR, jewelry inquiries, custom jewelry orders, wholesale jewelry, jewelry customer service, AGNIR support"
        url="https://agnir.co/contact"
        image="https://agnir.co/hero-bg.webp"
      />
      <Box 
        minH="100vh" 
        bgImage={`url(${process.env.REACT_APP_BG_URL})`}
        py={{ base: 10, lg: 20 }}
      >
        <Container maxW="container.xl">
          {/* Header Section */}
          <MotionBox
            textAlign="center"
            mb={{ base: 10, lg: 16 }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heading
              as="h1"
              size={{ base: "2xl", lg: "3xl" }}
              fontFamily="'Playfair Display', serif"
              fontWeight="700"
              color="gray.800"
              mb={4}
            >
              Let's Talk
            </Heading>
            <Text
              fontSize={{ base: "lg", lg: "xl" }}
              color="gray.600"
              fontFamily="'Inter', sans-serif"
              maxW="600px"
              mx="auto"
              lineHeight="1.6"
            >
              Have questions about our jewelry collection? We'd love to hear from you. 
              Send us a message and we'll respond as soon as possible.
            </Text>
          </MotionBox>

          {/* Main Content Grid */}
          <Grid
            templateColumns={{ base: '1fr', lg: '1fr 1.5fr' }}
            gap={{ base: 10, lg: 16 }}
            alignItems="start"
          >
            {/* Left Side - Contact Information */}
            <GridItem>
              <MotionBox
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  bg="white"
                  borderRadius="2xl"
                  p={{ base: 6, lg: 8 }}
                  boxShadow="0 10px 30px rgba(0, 0, 0, 0.1)"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <Heading
                    as="h3"
                    size="lg"
                    fontFamily="'Playfair Display', serif"
                    fontWeight="600"
                    color="gray.800"
                    mb={6}
                  >
                    Contact Information
                  </Heading>

                  <VStack spacing={6} align="stretch">
                    {contactMethods.map((method, index) => (
                      <MotionBox
                        key={method.label}
                        whileHover={{ scale: 1.02, x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Link
                          href={method.href}
                          isExternal
                          _hover={{ textDecoration: 'none' }}
                        >
                          <HStack
                            spacing={4}
                            p={4}
                            borderRadius="xl"
                            bg="gray.50"
                            _hover={{
                              bg: "gray.100",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                            }}
                            transition="all 0.3s ease"
                          >
                            <Box
                              p={3}
                              borderRadius="full"
                              bg="white"
                              boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
                            >
                              <Icon
                                as={method.icon}
                                boxSize={5}
                                color={method.color}
                              />
                            </Box>
                            <VStack align="start" spacing={1} flex={1}>
                              <Text
                                fontFamily="'Inter', sans-serif"
                                fontWeight="600"
                                color="gray.800"
                                fontSize="md"
                              >
                                {method.label}
                              </Text>
                              <Text
                                fontFamily="'Inter', sans-serif"
                                color="gray.600"
                                fontSize="sm"
                              >
                                {method.value}
                              </Text>
                            </VStack>
                          </HStack>
                        </Link>
                      </MotionBox>
                    ))}
                  </VStack>

                  <Divider my={8} />

                </Box>
              </MotionBox>
            </GridItem>

            {/* Right Side - Contact Form */}
            <GridItem>
              <MotionBox
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Box
                  bg="white"
                  borderRadius="2xl"
                  p={{ base: 6, lg: 8 }}
                  boxShadow="0 10px 30px rgba(0, 0, 0, 0.1)"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <Heading
                    as="h3"
                    size="lg"
                    fontFamily="'Playfair Display', serif"
                    fontWeight="600"
                    color="gray.800"
                    mb={6}
                  >
                    Send us a Message
                  </Heading>

                  <form onSubmit={handleSubmit}>
                    <VStack spacing={6}>
                      {/* Name Field */}
                      <FormControl isRequired isInvalid={fieldErrors.name}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Full Name
                        </FormLabel>
                        <Input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Enter your full name"
                          maxLength={100}
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          fontFamily="'Inter', sans-serif"
                          _focus={{
                            borderColor: "gray.400",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                          }}
                          _hover={{ borderColor: "gray.300" }}
                          bg="gray.50"
                          _focusVisible={{ bg: "white" }}
                        />
                        {fieldErrors.name && (
                          <Text color="red.500" fontSize="sm" mt={1} fontFamily="'Inter', sans-serif">
                            {fieldErrors.name}
                          </Text>
                        )}
                      </FormControl>

                      {/* Email Field */}
                      <FormControl isRequired isInvalid={fieldErrors.email}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Email Address
                        </FormLabel>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Enter your email address"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          fontFamily="'Inter', sans-serif"
                          _focus={{
                            borderColor: "gray.400",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                          }}
                          _hover={{ borderColor: "gray.300" }}
                          bg="gray.50"
                          _focusVisible={{ bg: "white" }}
                        />
                        {fieldErrors.email && (
                          <Text color="red.500" fontSize="sm" mt={1} fontFamily="'Inter', sans-serif">
                            {fieldErrors.email}
                          </Text>
                        )}
                      </FormControl>

                      {/* Phone Number Field */}
                      <FormControl isRequired isInvalid={fieldErrors.phone_number}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Phone Number
                        </FormLabel>
                        <Input
                          type="tel"
                          value={formData.phone_number}
                          onChange={(e) => handleInputChange('phone_number', e.target.value)}
                          placeholder="Enter 10-digit phone number"
                          maxLength={10}
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          fontFamily="'Inter', sans-serif"
                          _focus={{
                            borderColor: "gray.400",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                          }}
                          _hover={{ borderColor: "gray.300" }}
                          bg="gray.50"
                          _focusVisible={{ bg: "white" }}
                        />
                        {fieldErrors.phone_number && (
                          <Text color="red.500" fontSize="sm" mt={1} fontFamily="'Inter', sans-serif">
                            {fieldErrors.phone_number}
                          </Text>
                        )}
                        <Text color="gray.500" fontSize="xs" mt={1} fontFamily="'Inter', sans-serif">
                          10 digits only (without +91)
                        </Text>
                      </FormControl>

                      {/* Company Field (Optional) */}
                      <FormControl isInvalid={fieldErrors.company}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Company Name <Text as="span" color="gray.500">(Optional)</Text>
                        </FormLabel>
                        <Input
                          type="text"
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          placeholder="Enter your company name"
                          maxLength={150}
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          fontFamily="'Inter', sans-serif"
                          _focus={{
                            borderColor: "gray.400",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                          }}
                          _hover={{ borderColor: "gray.300" }}
                          bg="gray.50"
                          _focusVisible={{ bg: "white" }}
                        />
                        {fieldErrors.company && (
                          <Text color="red.500" fontSize="sm" mt={1} fontFamily="'Inter', sans-serif">
                            {fieldErrors.company}
                          </Text>
                        )}
                      </FormControl>

                      {/* How Did You Find Us */}
                      <FormControl isRequired isInvalid={fieldErrors.how_did_you_find_us}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          How did you find us?
                        </FormLabel>
                        <Select
                          value={formData.how_did_you_find_us}
                          onChange={(e) => handleInputChange('how_did_you_find_us', e.target.value)}
                          placeholder="Please select an option"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          fontFamily="'Inter', sans-serif"
                          _focus={{
                            borderColor: "gray.400",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                          }}
                          _hover={{ borderColor: "gray.300" }}
                          bg="gray.50"
                          _focusVisible={{ bg: "white" }}
                        >
                          {config?.how_did_you_find_us_choices?.map((choice) => (
                            <option key={choice.value} value={choice.value}>
                              {choice.label}
                            </option>
                          ))}
                        </Select>
                        {fieldErrors.how_did_you_find_us && (
                          <Text color="red.500" fontSize="sm" mt={1} fontFamily="'Inter', sans-serif">
                            {fieldErrors.how_did_you_find_us}
                          </Text>
                        )}
                      </FormControl>

                      {/* Message Field */}
                      <FormControl isRequired isInvalid={fieldErrors.message}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Message
                        </FormLabel>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          placeholder="Tell us about your inquiry..."
                          maxLength={config?.max_message_length || 1000}
                          rows={6}
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          fontFamily="'Inter', sans-serif"
                          _focus={{
                            borderColor: "gray.400",
                            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                          }}
                          _hover={{ borderColor: "gray.300" }}
                          bg="gray.50"
                          _focusVisible={{ bg: "white" }}
                        />
                        <HStack justify="space-between" mt={1}>
                          <Text color="red.500" fontSize="sm" fontFamily="'Inter', sans-serif">
                            {fieldErrors.message || ''}
                          </Text>
                          <Text color="gray.500" fontSize="xs" fontFamily="'Inter', sans-serif">
                            {formData.message.length}/{config?.max_message_length || 1000} characters
                          </Text>
                        </HStack>
                      </FormControl>

                      {/* Captcha */}
                      <FormControl isRequired isInvalid={captchaError}>
                        <FormLabel
                          fontFamily="'Inter', sans-serif"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Security Check
                        </FormLabel>
                        <HStack spacing={4} align="center">
                          <Box
                            bg="gray.100"
                            p={4}
                            borderRadius="xl"
                            border="2px solid"
                            borderColor="gray.200"
                            minW="120px"
                            textAlign="center"
                          >
                            <Text
                              fontFamily="'Inter', sans-serif"
                              fontWeight="600"
                              fontSize="lg"
                              color="gray.800"
                            >
                              {captcha.question} = ?
                            </Text>
                          </Box>
                          <Input
                            type="number"
                            value={captchaInput}
                            onChange={(e) => {
                              setCaptchaInput(e.target.value);
                              setCaptchaError(false);
                            }}
                            placeholder="Answer"
                            maxW="100px"
                            borderRadius="xl"
                            border="2px solid"
                            borderColor={captchaError ? "red.300" : "gray.200"}
                            fontFamily="'Inter', sans-serif"
                            _focus={{
                              borderColor: captchaError ? "red.400" : "gray.400",
                              boxShadow: `0 0 0 1px ${captchaError ? 'rgba(245, 101, 101, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`
                            }}
                            _hover={{ borderColor: captchaError ? "red.300" : "gray.300" }}
                            bg="gray.50"
                            _focusVisible={{ bg: "white" }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={generateCaptcha}
                            fontFamily="'Inter', sans-serif"
                            color="gray.600"
                            _hover={{ bg: "gray.100" }}
                          >
                            Refresh
                          </Button>
                        </HStack>
                        {fieldErrors.captcha && (
                          <Text color="red.500" fontSize="sm" mt={1} fontFamily="'Inter', sans-serif">
                            {fieldErrors.captcha}
                          </Text>
                        )}
                      </FormControl>

                      {/* Submit Button */}
                      <MotionButton
                        type="submit"
                        isLoading={isSubmitting}
                        loadingText="Sending Message..."
                        bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
                        color="white"
                        size="lg"
                        w="full"
                        h="54px"
                        borderRadius="xl"
                        fontFamily="'Inter', sans-serif"
                        fontWeight="600"
                        leftIcon={<FaPaperPlane />}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        _hover={{
                          bg: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
                        }}
                        _disabled={{
                          bg: "gray.400",
                          cursor: "not-allowed",
                          _hover: { bg: "gray.400" }
                        }}
                        boxShadow="0 4px 14px rgba(0, 0, 0, 0.15)"
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.1)"
                      >
                        Send Message
                      </MotionButton>
                    </VStack>
                  </form>
                </Box>
              </MotionBox>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Success Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(4px)" />
        <ModalContent 
          borderRadius="2xl"
          boxShadow="0 25px 50px rgba(0, 0, 0, 0.25)"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.1)"
          bg="white"
          mx={4}
        >
          <ModalHeader 
            fontFamily="'Playfair Display', serif"
            fontSize="xl"
            fontWeight="600"
            color="gray.800"
            textAlign="center"
            pt={8}
          >
            <VStack spacing={4}>
              <Box
                p={4}
                borderRadius="full"
                bg="green.50"
                border="2px solid"
                borderColor="green.200"
              >
                <Icon as={FaCheckCircle} boxSize={8} color="green.500" />
              </Box>
              Thank You!
            </VStack>
          </ModalHeader>
          <ModalCloseButton 
            color="gray.500"
            _hover={{ bg: "gray.100" }}
          />
          <ModalBody pb={6} textAlign="center">
            <VStack spacing={4}>
              <Text
                fontFamily="'Inter', sans-serif"
                color="gray.600"
                fontSize="lg"
                lineHeight="1.6"
              >
                Your message has been sent successfully! We've received your inquiry and will get back to you as soon as possible.
              </Text>
              <Text
                fontFamily="'Inter', sans-serif"
                color="gray.500"
                fontSize="sm"
              >
                Typically, we respond within 24 hours during business days.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={8}>
            <Button
              onClick={onClose}
              bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
              color="white"
              borderRadius="xl"
              px={8}
              fontFamily="'Inter', sans-serif"
              _hover={{
                bg: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ContactPage;